import { useCallback, useState } from 'react'

import { logger } from '@subaio/utils'

import { useAsync } from '../useAsync'

export type Getter<T> = (abortIfCancelled: () => void) => Promise<T>

type Updater<T> = (current: T, abortIfCancelled: () => void) => Awaitable<T>

type Setter<T> = (update: Awaitable<T> | Updater<T>) => Awaitable<void>

/**
 * A asynchronous resource as returned by `useAsyncResource`.
 */
export type AsyncResource<T> =
	| { status: 'loading'; value?: T }
	| { status: 'errored'; value?: T; error: unknown; refresh: () => Awaitable<void> }
	| { status: 'done'; value: T; set: Setter<T>; refresh: () => Awaitable<void> }

/**
 * A type helper for simpler async fetched values in other contexts. Added here to keep it in sync with AsyncResource,
 * but not part of this.
 */
export type AsyncValue<T> = { type: 'loading' } | { type: 'errored' } | { type: 'done'; value: T }

// Helper to fix typing since T could technically be a function
const isUpdater = <T>(update: Awaitable<T> | Updater<T>): update is Updater<T> => typeof update === 'function'

/**
 * Get some resource asynchronously, with the possibility to later update the
 * value. If the `getResource` function changes, all previous values will be
 * discarded, so use `useCallback` to ensure referential equality.
 *
 * To bail early from long-running processes, an `abortIfCancelled` function is
 * passed. This function will throw if `getResource` has changed, and catch the
 * exception itself. Note that it is not _necessary_ to call it, it's only meant
 * for bailing early in a multi-step async process.
 *
 * If the value should be updated, it is possible. Note that it should in
 * principle still represent the same resource, otherwise it would be better to
 * change the referential equality of `getResource`. Updates are executed
 * sequentially, and updaters are skipped if the referential equality of
 * `getResource` changes.
 */
export const useAsyncResource = <T>(getResource: Getter<T>): AsyncResource<T> => {
	const [state, setState] = useState<AsyncResource<T>>({ status: 'loading' })

	useAsync(
		useCallback(
			async (isCancelled) => {
				const cancelSignal = {}
				let lastPromise: Promise<T>
				const abortIfCancelled = () => {
					if (isCancelled()) throw cancelSignal
				}
				const updateValue = async (getValue: (current: T) => Awaitable<T>) => {
					try {
						lastPromise = Promise.resolve(lastPromise).then(
							(last) => {
								abortIfCancelled()
								return getValue(last)
							},
							() => {
								abortIfCancelled()
								// This should only happen if status is errored, and then `set` is not available
								return getValue(undefined as any)
							},
						)
						const value = await lastPromise
						abortIfCancelled()
						setState({
							status: 'done',
							value,
							set: (update) =>
								updateValue(isUpdater(update) ? (current) => update(current, abortIfCancelled) : () => update),
							refresh,
						})
					} catch (error) {
						if (isCancelled()) return
						setState((current) => ({
							status: 'errored',
							error,
							refresh,
							value: current.value,
						}))
						logger.error(error)
					}
				}

				const setLoading = () => setState((current) => ({ status: 'loading', value: current.value }))

				const refresh = () => {
					setLoading()
					return updateValue(() => getResource(abortIfCancelled))
				}

				await updateValue(() => getResource(abortIfCancelled))

				return () => {
					setLoading()
				}
			},
			[getResource],
		),
	)

	return state
}
